import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@apollo/client'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import Chip from '@material-ui/core/Chip'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import GET_GROUP_EVENT_BY_SLUG from 'pages/pga-coach/bookings/events/edit/query'
import { parseEvent } from 'utils/eventUtils'
import {
  blockTimeFromOccurrence,
  formatOccurrenceDate,
  formatOccurrenceTimeRange,
  getOccurrenceEventType,
  isCoachBlockTime,
  isGoogleBusyTime,
  occurrenceDisplayTitle,
  occurrenceParticipantLine,
  sessionBookingFromOccurrence,
} from './academy-schedule-event-utils'

const TYPE_LABELS = {
  Session: 'Private lesson',
  GroupEvent: 'Group event',
  CoachAvailabilityBlockTime: 'Blocked time',
  GoogleCalendarBusyTime: 'External calendar',
}

function ReadOnlyOccurrenceDetails({ occurrence, coach, onClose }) {
  const eventType = getOccurrenceEventType(occurrence)
  const title = occurrenceDisplayTitle(occurrence)
  const participant = occurrenceParticipantLine(occurrence)
  const typeLabel = TYPE_LABELS[eventType] || 'Calendar item'
  const block = isCoachBlockTime(occurrence)
    ? blockTimeFromOccurrence(occurrence)
    : null
  const session =
    eventType === 'Session'
      ? sessionBookingFromOccurrence(occurrence, coach)
      : null

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
          <Chip
            size="small"
            label={typeLabel}
            style={{ alignSelf: 'flex-start' }}
          />
          {coach?.name && (
            <Typography variant="body2" color="textSecondary">
              Coach: {coach.name}
            </Typography>
          )}
          <Typography variant="body1">
            {formatOccurrenceDate(occurrence)}
          </Typography>
          <Typography variant="body1">
            {formatOccurrenceTimeRange(occurrence)}
          </Typography>
          {occurrence?.subtitle && (
            <Typography variant="body2" color="textSecondary">
              {occurrence.subtitle}
            </Typography>
          )}
          {participant && (
            <Typography variant="body2">
              {eventType === 'GroupEvent' ? 'Registered' : 'Student'}:{' '}
              {participant}
            </Typography>
          )}
          {block?.blockNote && (
            <Typography variant="body2" color="textSecondary">
              Note: {block.blockNote}
            </Typography>
          )}
          {session?.isCanceled && (
            <Typography variant="body2" color="error">
              Canceled
            </Typography>
          )}
          {isGoogleBusyTime(occurrence) && (
            <Typography variant="body2" color="textSecondary">
              This time is blocked on the coach&apos;s linked external calendar.
            </Typography>
          )}
          {isCoachBlockTime(occurrence) && (
            <Typography variant="body2" color="textSecondary">
              To edit another coach&apos;s block time, use impersonation from
              Academy Coaches.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </>
  )
}

ReadOnlyOccurrenceDetails.propTypes = {
  occurrence: PropTypes.object.isRequired,
  coach: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

/**
 * Opens full group-event details when the slug resolves for the current user;
 * otherwise falls back to a read-only occurrence summary.
 */
function AcademyScheduleEventDialog({
  open,
  occurrence,
  coach,
  onClose,
  onEditGroupEvent,
}) {
  const slug = occurrence?.event?.slug
  const eventType = getOccurrenceEventType(occurrence)
  const isGroupEvent = eventType === 'GroupEvent' && slug

  const { data, loading } = useQuery(GET_GROUP_EVENT_BY_SLUG, {
    variables: { slug },
    skip: !open || !isGroupEvent,
    fetchPolicy: 'cache-and-network',
  })

  const fullGroupEvent = data?.currentUser?.groupEvents?.[0]
  const parsedGroupEvent = useMemo(() => {
    if (!fullGroupEvent) return null
    try {
      return parseEvent(fullGroupEvent)
    } catch {
      return null
    }
  }, [fullGroupEvent])

  if (!open || !occurrence) return null

  if (isGroupEvent && loading) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <LinearProgress />
      </Dialog>
    )
  }

  if (isGroupEvent && parsedGroupEvent) {
    return (
      <EventDetailsDialog
        isOpen="detail"
        setIsOpen={v => {
          if (v === false) onClose()
        }}
        event={parsedGroupEvent}
        onEditInSchedule={ev => {
          if (ev?.slug) onEditGroupEvent?.(ev.slug)
          onClose()
        }}
      />
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <ReadOnlyOccurrenceDetails
        occurrence={occurrence}
        coach={coach}
        onClose={onClose}
      />
    </Dialog>
  )
}

AcademyScheduleEventDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  occurrence: PropTypes.object,
  coach: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEditGroupEvent: PropTypes.func,
}

AcademyScheduleEventDialog.defaultProps = {
  occurrence: null,
  coach: null,
  onEditGroupEvent: null,
}

export default AcademyScheduleEventDialog
